import React, { useEffect, useState } from "react";
import "../assets/css/SignageOrders.css";
import { showSuccess, showError } from "toast/toast";

import { getAuthCookie } from "../utils/authCookies";

export type ConnectVendorModalVendor = {
  id: string;
  name: string;
  logo?: string;
};

type RequestOption = "credit_terms" | "special_pricing" | "private_catalog" | "priority_service" | "long_term_partnership";

const REQUEST_OPTIONS: { key: RequestOption; label: string }[] = [
  { key: "credit_terms", label: "Credit terms (Net-30 / Net-45)" },
  { key: "special_pricing", label: "Special or negotiated pricing" },
  { key: "private_catalog", label: "Access to private catalog" },
  { key: "priority_service", label: "Priority service handling" },
  { key: "long_term_partnership", label: "Long-term partnership" },
];

const ESTIMATED_VOLUME_OPTIONS = [
  { value: "", label: "Select volume..." },
  { value: "1-10", label: "1–10 orders/month" },
  { value: "11-50", label: "11–50 orders/month" },
  { value: "51-100", label: "51–100 orders/month" },
  { value: "100+", label: "100+ orders/month" },
];

type ServiceOrderOption = "products" | "installations" | "removals" | "bundled_services";

const SERVICE_ORDER_OPTIONS: { key: ServiceOrderOption; label: string }[] = [
  { key: "products", label: "Products" },
  { key: "installations", label: "Installations" },
  { key: "removals", label: "Removals" },
  { key: "bundled_services", label: "Bundled services" },
];

type ConnectVendorModalProps = {
  show: boolean;
  onClose: () => void;
  vendor: ConnectVendorModalVendor | null;
  userType: "agent" | "realtor";
  onSuccess?: () => void;
};

type UserBusinessData = {
  businessType: string;
  brokerageName: string;
  agentName: string;
  email: string;
  phone: string;
  businessAddress: string;
};

const defaultBusiness: UserBusinessData = {
  businessType: "Brokerage",
  brokerageName: "",
  agentName: "",
  email: "",
  phone: "",
  businessAddress: "",
};

const ConnectVendorModal: React.FC<ConnectVendorModalProps> = ({
  show,
  onClose,
  vendor,
  userType,
  onSuccess,
}) => {
  const userId = typeof window !== "undefined" ? getAuthCookie("userId") : null;
  const membershipId = typeof window !== "undefined" ? getAuthCookie("membershipId") : null;
  const organizationId = typeof window !== "undefined" ? getAuthCookie("organizationId") : null;
  const token = typeof window !== "undefined" ? getAuthCookie("sessionToken") : null;
  const API_URL = process.env.REACT_APP_API_URL;

  const [loadingUser, setLoadingUser] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [business, setBusiness] = useState<UserBusinessData>(defaultBusiness);
  const [phone, setPhone] = useState("");
  const [requests, setRequests] = useState<RequestOption[]>([]);
  const [estimatedVolume, setEstimatedVolume] = useState("");
  const [servicesTypicallyOrder, setServicesTypicallyOrder] = useState<ServiceOrderOption[]>([]);
  const [messageToVendor, setMessageToVendor] = useState("");

  const toggleRequest = (key: RequestOption) => {
    setRequests((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleServiceOrder = (key: ServiceOrderOption) => {
    setServicesTypicallyOrder((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  useEffect(() => {
    const detailsId = userType === "agent" ? membershipId : organizationId;
    if (!show || !API_URL || !token || !detailsId) return;
    setLoadingUser(true);
    setRequests([]);
    setEstimatedVolume("");
    setServicesTypicallyOrder([]);
    setMessageToVendor("");

    const detailsUrl =
      userType === "agent"
        ? `${API_URL}/organization-memberships/${detailsId}`
        : `${API_URL}/organizations/${detailsId}`;

    fetch(detailsUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((r) => r.json())
      .then((data: any) => {
        const profile = data?.profile || {};
        const owner = data?.owner || {};
        const loc = data?.address || profile?.address || {};
        const addressParts = [
          loc?.addressLine1 || loc?.address,
          loc?.city,
          loc?.stateCode || loc?.state,
          loc?.zip,
        ].filter(Boolean);
        const businessAddress = addressParts.length
          ? addressParts.join(", ")
          : "";

        if (userType === "agent") {
          // Membership response: personal fields at top level, org name nested
          setBusiness({
            businessType: "Agent",
            brokerageName: data?.organization?.name || data?.companyName || "",
            agentName: [data?.firstName, data?.lastName].filter(Boolean).join(" ") || "",
            email: data?.email || "",
            phone: data?.phone1 || data?.phone || loc?.phone || "",
            businessAddress: businessAddress || "",
          });
          setPhone(data?.phone1 || data?.phone || loc?.phone || "");
        } else {
          // Organization response: company at top level, contact person under owner/profile
          setBusiness({
            businessType: "Brokerage",
            brokerageName: profile?.companyName || data?.name || "",
            agentName: [owner?.firstName, owner?.lastName].filter(Boolean).join(" ") || "",
            email: owner?.email || data?.email || "",
            phone: owner?.phone1 || profile?.phone || loc?.phone || "",
            businessAddress: businessAddress || "",
          });
          setPhone(owner?.phone1 || profile?.phone || loc?.phone || "");
        }
      })
      .catch(() => setBusiness(defaultBusiness))
      .finally(() => setLoadingUser(false));
  }, [show, API_URL, token, membershipId, organizationId, userType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!API_URL || !token || !userId || !vendor) return;
    const supplierid = typeof window !== "undefined" ? localStorage.getItem("supplierId") : null;
    const payload = {
      supplierid: supplierid || vendor.id,
      businessType: business.businessType,
      brokerageName: business.brokerageName,
      agentName: business.agentName,
      email: business.email,
      phone: phone || business.phone,
      businessAddress: business.businessAddress,
      requests,
      estimatedOrderVolume: estimatedVolume || undefined,
      servicesTypicallyOrder: servicesTypicallyOrder.length ? servicesTypicallyOrder : undefined,
      messageToVendor: messageToVendor || undefined,
    };

    setSubmitting(true);
    try {
      const resp = await fetch(`${API_URL}/Signage/requestsent/${userType}/${userId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error((json as any)?.error || (json as any)?.message || "Request failed.");
      showSuccess("Connection request sent successfully.");
      onSuccess?.();
      onClose();
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to send connection request.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (show) window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [show, onClose]);

  if (!show) return null;

  const initials = vendor?.name?.replace(/[^a-zA-Z]/g, "").slice(0, 2).toUpperCase() || "V";

  return (
    <div className="modal-overlay active" onClick={onClose} role="presentation">
      <div
        className="connect-modal active"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="connect-modal-title"
      >
        <div className="connect-modal-header">
          <div className="connect-modal-title-section">
            <h2 id="connect-modal-title" className="connect-modal-title">
              Connect with Vendor
            </h2>
            {/* {vendor && (
              <div className="connect-modal-vendor">
                <div
                  className="connect-modal-vendor-logo"
                  style={{
                    background: vendor.logo ? `url(${vendor.logo}) center/cover` : "var(--primary, #1e5db8)",
                  }}
                >
                  {!vendor.logo && initials}
                </div>
                <span className="connect-modal-vendor-name">{vendor.name}</span>
              </div>
            )} */}
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="connect-modal-content">
          <div className="connect-intro">
            <span className="connect-intro-icon" aria-hidden>💡</span>
            Request a deeper working relationship with this vendor to unlock better pricing, credit terms, and priority services. You can continue placing orders while the vendor reviews your request.
          </div>

          <div className="form-section">
            <div className="form-section-title">
              <span className="form-section-number">1</span>
              Your Business Details
            </div>
            {loadingUser ? (
              <div className="form-grid">
                <div className="form-group full-width">
                  <span className="form-label">Loading…</span>
                </div>
              </div>
            ) : (
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Business type</label>
                  <input
                    type="text"
                    className="form-input readonly"
                    readOnly
                    value={business.businessType}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Brokerage name</label>
                  <input
                    type="text"
                    className="form-input readonly"
                    readOnly
                    value={business.brokerageName}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Agent name</label>
                  <input
                    type="text"
                    className="form-input readonly"
                    readOnly
                    value={business.agentName}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-input readonly"
                    readOnly
                    value={business.email}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone number</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div className="form-group full-width">
                  <label className="form-label">Business address</label>
                  <input
                    type="text"
                    className="form-input readonly"
                    readOnly
                    value={business.businessAddress}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="form-section">
            <div className="form-section-title">
              <span className="form-section-number">2</span>
              What are you requesting?
            </div>
            <div className="checkbox-row">
              {REQUEST_OPTIONS.map(({ key, label }) => (
                <div
                  key={key}
                  className={`checkbox-item ${requests.includes(key) ? "checked" : ""}`}
                  onClick={() => toggleRequest(key)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && toggleRequest(key)}
                >
                  <input
                    type="checkbox"
                    id={`req-${key}`}
                    checked={requests.includes(key)}
                    onChange={() => toggleRequest(key)}
                  />
                  <label htmlFor={`req-${key}`}>{label}</label>
                </div>
              ))}
            </div>
          </div>

          <div className="form-section">
            <div className="form-section-title">
              <span className="form-section-number">3</span>
              <span>
                Estimated Order Volume
                <span className="form-section-optional">(Optional but recommended)</span>
              </span>
            </div>
            <div className="connect-volume-row">
              <div className="form-group">
                <label className="form-label form-label-uppercase">Estimated monthly order volume</label>
                <select
                  className="form-select"
                  value={estimatedVolume}
                  onChange={(e) => setEstimatedVolume(e.target.value)}
                  aria-label="Estimated monthly order volume"
                >
                  {ESTIMATED_VOLUME_OPTIONS.map((opt) => (
                    <option key={opt.value || "empty"} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label form-label-uppercase">Services you typically order</label>
                <div className="connect-services-grid">
                  {SERVICE_ORDER_OPTIONS.map(({ key, label }) => (
                    <div
                      key={key}
                      className={`checkbox-item ${servicesTypicallyOrder.includes(key) ? "checked" : ""}`}
                      onClick={() => toggleServiceOrder(key)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === "Enter" && toggleServiceOrder(key)}
                    >
                      <input
                        type="checkbox"
                        id={`service-${key}`}
                        checked={servicesTypicallyOrder.includes(key)}
                        onChange={() => toggleServiceOrder(key)}
                      />
                      <label htmlFor={`service-${key}`}>{label}</label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="form-section-title">
              <span className="form-section-number">4</span>
              Message to Vendor (Optional)
            </div>
            <textarea
              className="form-textarea"
              value={messageToVendor}
              onChange={(e) => setMessageToVendor(e.target.value)}
              placeholder="Briefly share your expected volume, locations served, or any specific needs that may help the vendor review your request."
              rows={4}
            />
          </div>

          <div className="form-submit-section">
            <button
              type="submit"
              className="btn-submit-connect"
              disabled={submitting || loadingUser}
            >
              {submitting ? "Sending…" : "Submit Connection Request"}
            </button>
            <p className="form-helper-text">The vendor will review your request. You can continue placing orders during this time.</p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConnectVendorModal;
